import React, { useCallback } from 'react';

import { useTranslation } from '@modernloop/i18n';
import { FilterSelect, defaultRenderGroup } from '@modernloop/shared/components';
import { PublicError } from '@modernloop/shared/helper-components';
import {
  AutocompleteRenderGroupParams,
  Chip,
  ListItemButton,
  ListSubheader,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { find } from 'lodash';

import { AtsInterviewDefinitionFragment } from 'src/generated/mloop-graphql';

import ImageBlock from 'src/components/ImageBlock';

import { useOrgAtsService } from 'src/hooks/atsService';
import { ScorecardGroupByStrategy } from 'src/hooks/atsService/AtsService';
import { getAtsInterviewDefinitionFields } from 'src/hooks/atsService/util';

import { getScorecardGroupName } from './scorecardGrouping';
import useAtsScorecardOptions, { AtsScorecardOption } from './useAtsScorecardOptions';

export type { AtsScorecardOption } from './useAtsScorecardOptions';

export interface BaseAtsScorecardProps {
  result: ReturnType<typeof useAtsScorecardOptions>;
  atsInterviewDefinitionId?: string;
  /**
   * This job stage's own remote id. A definition can be pinned from any stage's dropdown, but a
   * debrief renders in the ATS under the definition's *own* stage — so a mismatch here means the
   * write will land somewhere other than where this picker lives.
   */
  expectedAtsJobStageId?: string;
  selectScorecard: (scorecard: AtsInterviewDefinitionFragment) => void;
}

/**
 * Returns the ATS-specific scorecard group label resolver for scorecard dropdowns.
 */
export const useGroupBy = () => {
  const { t } = useTranslation();
  const atsService = useOrgAtsService();
  const groupBy = useCallback(
    (option: AtsInterviewDefinitionFragment) => {
      return getScorecardGroupName({
        option,
        scorecardGroupByStrategy: atsService?.scorecardGroupByStrategy ?? ScorecardGroupByStrategy.AtsJobStage,
        scorecardGroupByKey: atsService?.scorecardGroupByKey ?? null,
        t,
      });
    },
    [atsService, t]
  );
  return groupBy;
};

export const useNoScorecardLabel = () => {
  const { t } = useTranslation();
  const atsService = useOrgAtsService();
  return t(atsService?.noScorecardLabelTranslationKey || 'hiring-dashboard-common:common.ats.noScorecardFallback');
};

/**
 * Returns the ATS-specific title for the scorecard selection dialog.
 */
export const useScorecardSelectTitle = () => {
  const { t } = useTranslation();
  const atsService = useOrgAtsService();
  return t(atsService?.scorecardSelectTitleTranslationKey || 'hiring-dashboard-nav:nav.scorecards.selectTitle');
};

/**
 * Returns the ATS-specific search placeholder for the scorecard selection dialog.
 */
export const useScorecardSearchPlaceholder = () => {
  const { t } = useTranslation();
  const atsService = useOrgAtsService();
  return t(
    atsService?.scorecardSearchPlaceholderTranslationKey || 'hiring-dashboard-nav:nav.scorecards.searchPlaceholder'
  );
};

/**
 * Returns the ATS-specific search placeholder for compact scorecard popovers.
 */
export const useScorecardSearchPlaceholderEllipsis = () => {
  const { t } = useTranslation();
  const atsService = useOrgAtsService();
  return t(
    atsService?.scorecardSearchPlaceholderEllipsisTranslationKey ||
      'hiring-dashboard-nav:nav.scorecards.searchPlaceholderEllipsis'
  );
};

export const useRenderOption = () => {
  const { t } = useTranslation();
  const noScorecardLabel = useNoScorecardLabel();
  const renderOption = useCallback(
    (option: AtsScorecardOption) => {
      const estimatedMinutes = getAtsInterviewDefinitionFields(option.atsFields, 'estimatedMinutes');
      const title = option.name ?? noScorecardLabel;
      const titleNode = option.isDefault ? (
        <Stack direction="row" spacing={0.75} alignItems="center" minWidth={0}>
          <Tooltip title={title}>
            <Typography noWrap>{title}</Typography>
          </Tooltip>
          <Chip
            size="small"
            label={t('hiring-dashboard-common:common.default')}
            sx={{ height: 20, flexShrink: 0, '& .MuiChip-label': { px: 0.75 } }}
          />
        </Stack>
      ) : (
        title
      );
      const caption = estimatedMinutes
        ? t('hiring-dashboard-nav:nav.scorecards.mins', { minutes: estimatedMinutes })
        : undefined;
      return (
        <ImageBlock
          noWrap
          dataTestId={option.atsId || 'ats-scorecard-render-option-no-scorecard'}
          title={titleNode}
          caption={caption}
        />
      );
    },
    [noScorecardLabel, t]
  );

  return renderOption;
};

export const useGetChildSize = () => {
  const atsService = useOrgAtsService();
  const getChildSize = useCallback(
    (child: React.ReactNode) => {
      if (!atsService?.showEstimatedMinutes || (React.isValidElement(child) && child.type === ListSubheader)) {
        return 32;
      }
      return 48;
    },
    [atsService]
  );
  return getChildSize;
};

export const useRenderGroup = () => {
  const renderGroup = useCallback((params: AutocompleteRenderGroupParams) => {
    return defaultRenderGroup(params);
  }, []);

  return renderGroup;
};

export const BaseAtsScorecard = ({
  result,
  atsInterviewDefinitionId,
  expectedAtsJobStageId,
  selectScorecard,
}: BaseAtsScorecardProps): JSX.Element | null => {
  const { t } = useTranslation();
  const { loading, error, options } = result;
  const renderGroup = useRenderGroup();
  const renderOptionImageBlock = useRenderOption();
  const getChildSize = useGetChildSize();
  const groupBy = useGroupBy();
  const noScorecardLabel = useNoScorecardLabel();

  if (loading) {
    return (
      <Typography variant="body2" color="text.disabled">
        {t('hiring-dashboard-nav:nav.scorecards.loading')}
      </Typography>
    );
  }
  if (error) {
    return <PublicError error={error} errorTitle={t('hiring-dashboard-nav:nav.scorecards.errorPrefix')} />;
  }

  const selectedScorecard: AtsScorecardOption | undefined =
    (atsInterviewDefinitionId != null && find(options, (scorecard) => scorecard.atsId === atsInterviewDefinitionId)) ||
    undefined;
  const hasStageMismatch = Boolean(
    expectedAtsJobStageId &&
    selectedScorecard?.atsJobStageId &&
    selectedScorecard.atsJobStageId !== expectedAtsJobStageId
  );
  const fallbackScorecardLabel = options[0]?.name ?? noScorecardLabel;
  const selectedScorecardLabel = selectedScorecard?.name ?? fallbackScorecardLabel;
  const collapsedLabel = selectedScorecard?.isDefault ? (
    <Stack direction="row" spacing={0.75} alignItems="center" minWidth={0}>
      <Tooltip title={selectedScorecardLabel}>
        <Typography noWrap>{selectedScorecardLabel}</Typography>
      </Tooltip>
      <Chip
        size="small"
        label={t('hiring-dashboard-common:common.default')}
        sx={{ height: 20, flexShrink: 0, '& .MuiChip-label': { px: 0.75 } }}
      />
    </Stack>
  ) : (
    selectedScorecardLabel
  );

  const handleScorecardSelect = (option: AtsInterviewDefinitionFragment) => {
    selectScorecard(option);
  };
  const renderOption = (props: React.HTMLAttributes<HTMLElement>, option: AtsScorecardOption) => {
    // Spreading the real option props (role, onClick, onMouseMove, data-option-index) directly onto
    // ListItemButton — rather than onto a wrapping ListItem — is what lets the option actually
    // receive hover and keyboard-focus highlighting: FilterList drives Autocomplete's headless
    // useAutocomplete() hook, whose highlight state is applied by imperatively adding a class to
    // whichever DOM node carries data-option-index, and only ListItemButton's own styles react to
    // that class. Matches the one sibling picker (AtsInterviewDefinitionSelect) that already does
    // this correctly.
    return (
      <ListItemButton
        {...props}
        data-testid="ats-scorecard-option"
        data-scorecard-name={option.name ?? 'none'}
        data-scorecard-id={option.atsId}
        key={option.atsId}
      >
        {renderOptionImageBlock(option)}
      </ListItemButton>
    );
  };

  return (
    <Stack rowGap={0.5}>
      <FilterSelect
        selectDataTestId="ats-scorecard-select"
        filterListDataTestId="ats-scorecard-filter-list"
        single
        placeholderText={t('hiring-dashboard-nav:nav.scorecards.searchPlaceholderDots')}
        getLabel={() => collapsedLabel}
        options={options}
        groupBy={groupBy}
        renderGroup={renderGroup}
        renderOption={renderOption}
        getChildSize={getChildSize}
        getOptionLabel={(scorecard) => scorecard.name ?? noScorecardLabel}
        onChange={(_, value) => handleScorecardSelect(value[0])}
      />
      {hasStageMismatch && (
        <Typography variant="caption" color="error" data-testid="ats-scorecard-stage-mismatch-warning">
          {t('hiring-dashboard-settings-scheduling:settings.scheduling.debriefAtsWrite.stageMismatchWarning')}
        </Typography>
      )}
    </Stack>
  );
};

export type AtsScorecardProps = Omit<BaseAtsScorecardProps, 'result'> & { atsJobId: string | null };

const AtsScorecard = ({ atsJobId, ...rest }: AtsScorecardProps): JSX.Element => {
  const result = useAtsScorecardOptions(atsJobId || undefined);
  return <BaseAtsScorecard result={result} {...rest} />;
};

export default AtsScorecard;
